import React, { useState } from 'react';
import { GoogleLogin } from '@react-oauth/google';
import axios from 'axios';
import CONFIG from '../config';

// Login page for Bill Management.
// Supports TWO ways to log in:
//   1) Email + password  — checked against the existing rate-system login
//   2) Sign in with Google — checked against the SAME rate-system login
// Both ways end the same way: a session token is saved as "fxToken",
// then App.js is told to continue.
function Login({ onLogin }) {
  const [email, setEmail]       = useState('');
  const [password, setPassword] = useState('');
  const [error, setError]       = useState('');
  const [busy, setBusy]         = useState(false);

  // Pulls the token out of the server's reply, no matter what it's named.
  const extractToken = (data) => {
    return (
      data.token || data.accessToken || data.sessionToken ||
      (data.data && (data.data.token || data.data.accessToken)) || ''
    );
  };

  const handleLogin = async () => {
    setError('');
    if (!email || !password) {
      setError('Please enter both email and password.');
      return;
    }
    setBusy(true);
    try {
      const res = await axios.post(
        `${CONFIG.AUTH_API}/users/login`,
        { email: email.trim(), password: password }
      );

      const data = res.data || {};
      const token = extractToken(data);

      if (!token) {
        setError('Login succeeded but no session token was returned. Please tell support.');
        setBusy(false);
        return;
      }

      localStorage.setItem('fxToken', token);
      if (onLogin) onLogin(token);
    } catch (err) {
      const status = err?.response?.status;
      if (status === 401 || status === 400) {
        setError('Invalid email or password.');
      } else if (err?.message?.includes('Network')) {
        setError('Cannot reach the login server. Please check your connection.');
      } else {
        setError('Login failed. Please try again.');
      }
      setBusy(false);
    }
  };

  // Runs when Google successfully confirms who the person is.
  const handleGoogleSuccess = async (credentialResponse) => {
    setError('');
    setBusy(true);
    try {
      const res = await axios.post(
        `${CONFIG.AUTH_API}/users/google-login`,
        { googleToken: credentialResponse.credential }
      );

      const data = res.data || {};

      if (data.success === false) {
        setError(data.message || 'Your account is pending admin approval.');
        setBusy(false);
        return;
      }

      const token = extractToken(data);
      if (!token) {
        setError('Google login succeeded but no session token was returned. Please tell support.');
        setBusy(false);
        return;
      }

      localStorage.setItem('fxToken', token);
      if (onLogin) onLogin(token);
    } catch (err) {
      setError('Google login failed. Please try again.');
      setBusy(false);
    }
  };

  const onKeyDown = (e) => {
    if (e.key === 'Enter') handleLogin();
  };

  return (
    <div style={styles.wrap}>
      <div style={styles.card}>
        <div style={styles.brandRow}>
          <div style={styles.logoBox}>FX</div>
          <div>
            <div style={styles.brandTitle}>FX CAPITAL INDIA</div>
            <div style={styles.brandSub}>Bill Management Portal</div>
          </div>
        </div>

        <div style={styles.divider} />

        {/* Google Sign-In button */}
        <div style={styles.googleWrap}>
          <GoogleLogin
            onSuccess={handleGoogleSuccess}
            onError={() => setError('Google login failed')}
            text="signin_with"
            shape="rectangular"
            theme="filled_black"
            width="340"
          />
        </div>

        <div style={styles.orRow}>
          <div style={styles.orLine} />
          <span style={styles.orText}>OR</span>
          <div style={styles.orLine} />
        </div>

        <label style={styles.label}>EMAIL ADDRESS</label>
        <input
          style={styles.input}
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          onKeyDown={onKeyDown}
          placeholder="you@example.com"
          autoFocus
        />

        <label style={styles.label}>PASSWORD</label>
        <input
          style={styles.input}
          type="password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          onKeyDown={onKeyDown}
          placeholder="Your password"
        />

        {error && <div style={styles.error}>{error}</div>}

        <button style={{ ...styles.button, opacity: busy ? 0.7 : 1 }}
          onClick={handleLogin} disabled={busy}>
          {busy ? 'Signing in…' : 'SIGN IN'}
        </button>

        <div style={styles.footer}>Secure access — FX Capital India</div>
      </div>
    </div>
  );
}

const styles = {
  wrap: {
    minHeight: '100vh', display: 'flex', alignItems: 'center',
    justifyContent: 'center', background: '#0a0f1c',
    fontFamily: 'system-ui, Arial, sans-serif', padding: '20px',
  },
  card: {
    width: '100%', maxWidth: '420px', background: '#131c2e',
    border: '1px solid #1f2d45', borderRadius: '16px', padding: '36px',
    boxShadow: '0 20px 60px rgba(0,0,0,0.5)',
  },
  brandRow: { display: 'flex', alignItems: 'center', gap: '14px' },
  logoBox: {
    width: '54px', height: '54px', borderRadius: '12px',
    background: 'linear-gradient(135deg,#c9a227,#8a6d1a)', color: '#1a1a1a',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    fontWeight: 800, fontSize: '20px',
  },
  brandTitle: { color: '#c9a227', fontWeight: 700, letterSpacing: '1px', fontSize: '18px' },
  brandSub: { color: '#7f8da3', fontSize: '13px', marginTop: '2px' },
  divider: { height: '1px', background: '#1f2d45', margin: '24px 0' },
  googleWrap: { display: 'flex', justifyContent: 'center', marginBottom: '20px' },
  orRow: { display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '20px' },
  orLine: { flex: 1, height: '1px', background: '#1f2d45' },
  orText: { fontSize: '12px', color: '#5a6a82' },
  label: { display: 'block', color: '#7f8da3', fontSize: '11px',
    fontWeight: 700, letterSpacing: '1px', marginBottom: '6px', marginTop: '14px' },
  input: {
    width: '100%', padding: '12px 14px', borderRadius: '8px',
    border: '1px solid #2a3a55', background: '#e8edf5', color: '#111',
    fontSize: '15px', boxSizing: 'border-box',
  },
  error: {
    marginTop: '16px', padding: '10px 14px', borderRadius: '8px',
    background: 'rgba(220,38,38,0.12)', border: '1px solid rgba(220,38,38,0.4)',
    color: '#f87171', fontSize: '14px',
  },
  button: {
    width: '100%', marginTop: '22px', padding: '14px',
    borderRadius: '8px', border: 'none', cursor: 'pointer',
    background: 'linear-gradient(135deg,#c9a227,#8a6d1a)', color: '#1a1a1a',
    fontWeight: 800, fontSize: '15px', letterSpacing: '1px',
  },
  footer: { textAlign: 'center', color: '#5a6a82', fontSize: '12px', marginTop: '20px' },
};

export default Login;